import { ChangeEvent, MouseEvent, useContext, useEffect, useRef, useState } from 'react';
import { Box, DialogActions, DialogContent, TextField } from '@mui/material';
import { MetadataError } from '@ep/error-handling';
import { FirebaseError } from 'firebase/app';
import {
  PhoneAuthCredential,
  PhoneAuthProvider,
  PhoneInfoOptions,
  PhoneMultiFactorAssertion,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  multiFactor
} from 'firebase/auth';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { MFA_TIMEOUT_MS } from '../../../env';
import { auth } from '../../../lib/google/firebase';
import { validatePhoneNumber } from '../../../lib/validation/text_validation';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { BodyText, Subheading } from '../../shared/typography/PrimaryTypography';
import { LoginState } from './LoginModal';
import { FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';

const SOURCE = 'EnrollSmsMfa.tsx';

/**
 * Handles the enrollment of client cellphone SMS Multifactor.
 * @param {(authenticated: boolean): void} setAuthenticated Sets the global authenticated state of the application
 * @param {(): void} closeLogin Closes the sign in process completely and resets it to initial sign in step.
 * @param {(login_state: LoginState): void} handleLoginState Handles the changing of modal state to various parts of the login flow.
 * @returns {JSX.Element}
 */
const EnrollSmsMfa = ({
  setAuthenticated,
  closeLogin,
  handleLoginState
}: {
  setAuthenticated: (authenticated: boolean) => void;
  closeLogin: () => void;
  handleLoginState: (login_state: LoginState) => void;
}): JSX.Element => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [enrollConfirmed, setEnrollConfirmed] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const [validCode, setValidCode] = useState<boolean>(false);
  const [verificationId, setVerificationId] = useState<string>('');
  const [userNumber, setUserNumber] = useState<string>('');
  const [hasExistingUserNumber, setHasExistingUserNumber] = useState<boolean>(false);
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState<boolean>(false);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  // Clears the RecaptchaVerifier and replaces the container element with a fresh one so it can be re-used.
  const resetEnrollRecaptcha = (): void => {
    recaptchaVerifierRef.current?.clear();
    recaptchaVerifierRef.current = null;
    document.getElementById('sms-enroll-recaptcha-container')?.remove();
    const new_container: HTMLDivElement = document.createElement('div');
    new_container.id = 'sms-enroll-recaptcha-container';
    document.body.appendChild(new_container);
  };

  // Handles returning to enrollment selection screen.
  const handleBack = (): void => {
    handleLoginState('mfa_selection');
  };

  // Handles sending of SMS code to users registered number.
  const handleCodeSend = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    (async () => {
      try {
        const phone_auth_provider: PhoneAuthProvider = new PhoneAuthProvider(auth);
        if (auth.currentUser != null) {
          resetEnrollRecaptcha();
          const phone_info_options: PhoneInfoOptions = await multiFactor(auth.currentUser)
            .getSession()
            .then((multiFactorSession) => {
              return {
                phoneNumber: userNumber,
                session: multiFactorSession
              };
            })
            .catch((error: unknown) => {
              throw new Error((error as FirebaseError).code);
            });
          const recaptcha_verifier: RecaptchaVerifier = new RecaptchaVerifier(
            auth,
            'sms-enroll-recaptcha-container',
            {
              size: 'invisible'
            }
          );
          recaptchaVerifierRef.current = recaptcha_verifier;
          await phone_auth_provider
            .verifyPhoneNumber(phone_info_options, recaptcha_verifier)
            .then(function (verificationId) {
              setVerificationId(verificationId);
              setEnrollConfirmed(true);
            })
            .catch((err) => {
              resetEnrollRecaptcha();
              if ((err as FirebaseError).code == 'auth/requires-recent-login') {
                const tracking_id: string = generateUuidV4();
                log(
                  'error',
                  new MetadataError(err, { 'auth.currentUser': auth.currentUser }, tracking_id)
                );
                setFeedbackData({
                  message: `Login wait time expired for Two-Factor Enrollment, please sign in again to register a phone number. Tracking ID: ${tracking_id}`,
                  state: true,
                  type: 'error'
                });
                auth.signOut();
              } else if (err.message.includes('auth/too-many-requests')) {
                const tracking_id: string = generateUuidV4();
                log(
                  'error',
                  new MetadataError(err, { 'auth.currentUser': auth.currentUser }, tracking_id)
                );
                setFeedbackData({
                  message: `Too many requests attempted, please try again later. Tracking ID: ${tracking_id}`,
                  state: true,
                  type: 'error'
                });
              } else {
                const tracking_id: string = generateUuidV4();
                log(
                  'error',
                  new MetadataError(err, { 'auth.currentUser': auth.currentUser }, tracking_id)
                );
                setFeedbackData({
                  message: `Failed to send verification code, invalid phone number format. Tracking ID: ${tracking_id}`,
                  state: true,
                  type: 'error'
                });
              }
            });
        }
      } catch (error: unknown) {
        throw new Error(
          error instanceof Error
            ? error.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleCodeSend')
        );
      }
    })().catch((error: unknown) => {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          error instanceof Error
            ? error.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleCodeSend'),
          { 'auth.currentUser': auth.currentUser },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Failed to send verification code. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    });
  };

  // Handles authenticating sent code to finalize enrollment of SMS MFA with account.
  const handleNumberRegistration = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.preventDefault();
    try {
      if (auth.currentUser != null) {
        const cred: PhoneAuthCredential = PhoneAuthProvider.credential(verificationId, code);
        const multi_factor_assertion: PhoneMultiFactorAssertion =
          PhoneMultiFactorGenerator.assertion(cred);
        await multiFactor(auth.currentUser).enroll(
          multi_factor_assertion,
          'My personal phone number'
        );
        setFeedbackData({
          message:
            'Number has been succesfully verified, please note that this number will be used for verification on all future logins to this platform.',
          state: true,
          type: 'success'
        });
        setAuthenticated(true);
        closeLogin();
      } else {
        setEnrollConfirmed(false);
        setVerificationId('');
        setCode('');
        setFeedbackData({
          message: 'Session expired, please re-enter your phone number to try again.',
          state: true,
          type: 'error'
        });
        return;
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      let error_message = `Failed to register MFA phone number: Tracking ID: ${tracking_id}`;
      if (err instanceof Error && 'code' in err) {
        if (err.code === 'auth/invalid-verification-code') {
          error_message = `Incorrect verification code, please try again! : Tracking ID: ${tracking_id}`;
        }
      }
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleNumberRegistration'),
          { 'auth.currentUser': auth.currentUser },
          tracking_id
        )
      );
      setFeedbackData({
        message: error_message,
        state: true,
        type: 'error'
      });
    }
  };

  // Handles setting of users registered number to the previewed numbers state in the modal on first render.
  useEffect(() => {
    if (auth.currentUser?.phoneNumber) {
      setUserNumber(auth.currentUser.phoneNumber);
      setHasExistingUserNumber(true);
    }
  }, []);

  // Validates the phone number the user enters when no number is pre-populated.
  useEffect(() => {
    const timer: NodeJS.Timeout = setTimeout(() => {
      setIsValidPhoneNumber(validatePhoneNumber(userNumber, '^\\+[1-9][0-9]{7,14}$'));
    }, 250);
    return () => clearTimeout(timer);
  }, [userNumber]);

  // Handles debounce time of user entering the code into the modal.
  useEffect(() => {
    setTimeout(() => {
      setValidCode(code.length > 0);
    }, 250);
    return () => {
      null;
    };
  }, [code]);

  // Handles closing of the MFA enrollment before it times out to force user to restart process
  useEffect(() => {
    const timeout_id: NodeJS.Timeout = setTimeout(async () => {
      closeLogin();
    }, MFA_TIMEOUT_MS);
    return () => clearTimeout(timeout_id);
  }, []);

  // Creates the recaptcha container in document.body on mount and removes it on unmount.
  useEffect(() => {
    const container: HTMLDivElement = document.createElement('div');
    container.id = 'sms-enroll-recaptcha-container';
    document.body.appendChild(container);
    return () => {
      recaptchaVerifierRef.current?.clear();
      recaptchaVerifierRef.current = null;
      document.getElementById('sms-enroll-recaptcha-container')?.remove();
    };
  }, []);

  return (
    <Box>
      <Subheading weight="Medium" sx={{ mb: 2 }}>
        SMS Two-Factor Enrollment
      </Subheading>
      {!enrollConfirmed && (
        <Box>
          {hasExistingUserNumber ? (
            <>
              <BodyText sx={{ mb: 2 }}>
                Confirm correct registered phone number{' '}
                <b>
                  {userNumber.slice(0, -4).replace(/./g, '*')}
                  {userNumber.slice(-4)}
                </b>{' '}
                to send SMS to.
              </BodyText>
              <BodyText sx={{ mb: 4 }}>
                If this is incorrect please contact Energy Partners, otherwise please click confirm
                below.
              </BodyText>
            </>
          ) : (
            <>
              <BodyText sx={{ mb: 2 }}>
                Enter your phone number in international format (e.g. +27821234567) to enroll SMS
                two-factor authentication.
              </BodyText>
              <DialogContent sx={{ px: 0 }}>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Phone Number"
                  type="tel"
                  fullWidth
                  variant="outlined"
                  value={userNumber}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setUserNumber(event.target.value)
                  }
                  error={userNumber.length > 0 && !isValidPhoneNumber}
                  helperText={
                    userNumber.length > 0 && !isValidPhoneNumber
                      ? 'Enter a valid international phone number starting with +'
                      : ''
                  }
                  sx={{ mb: 2 }}
                />
              </DialogContent>
            </>
          )}
          <DialogActions sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <PrimaryButton
              onClick={handleCodeSend}
              id="send-code-button"
              disabled={!hasExistingUserNumber && !isValidPhoneNumber}
              sx={{ minWidth: 180 }}
            >
              Confirm
            </PrimaryButton>
            <GhostButton
              onClick={handleBack}
              id="send-code-button"
              outlined
              dark
              sx={{ minWidth: 180 }}
            >
              Back
            </GhostButton>
          </DialogActions>
        </Box>
      )}
      {enrollConfirmed && (
        <>
          <BodyText sx={{ px: 3, py: 1 }}>
            Please enter the code sent to the phone number being verified.
          </BodyText>
          <form>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                id="name"
                label="Verification Code"
                type="text"
                fullWidth
                variant="outlined"
                sx={{ my: 4 }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setCode(event.target.value)}
              />
            </DialogContent>

            <DialogActions sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <PrimaryButton
                data-testid="verify-button"
                onClick={handleNumberRegistration}
                disabled={!validCode}
                sx={{ minWidth: '200px' }}
                type="submit"
              >
                Verify
              </PrimaryButton>
              <GhostButton
                onClick={handleBack}
                id="send-code-button"
                outlined
                dark
                sx={{ minWidth: 180 }}
              >
                Back
              </GhostButton>
            </DialogActions>
          </form>
        </>
      )}
    </Box>
  );
};

export default EnrollSmsMfa;
